import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import Sidebar from "../../../components/layout/Sidebar";
import { Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { BASE_URL } from "../../../env";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import {
  generateLoanSummaryForMember,
  getReportWindow,
  getFirstAndLastEmiDates,
  getDisbursementAmount,
  formatAmount,
  resolveEffectiveStartDate,
} from "../../../utils/utils";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

function GplOutstandingReport() {
  const { adminUser } = useAdminAuth();

  // State variables
  const [reportData, setReportData] = useState([]);
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null);
  const [fromDateDisbursed, setFromDateDisbursed] = useState("");
  const [toDateDisbursed, setToDateDisbursed] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Constants
  const fromDate = "2000-01-01"; // Start from early enough to catch all historical data
  // The date the balance is reported "as at". Master uses the same picker value
  // for this, so both reports answer the same question for a historical date.
  // Previously hardcoded to today, which made Outstanding disagree with Master
  // for any past date (bugfix.md Bug 16).
  const { asAtDate } = getReportWindow(fromDateDisbursed, toDateDisbursed);
  const itemsPerPage = 20;

  // Memoized calculations
  const flattenedData = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];

    // Deduplicate first
    const deduplicatedMap = reportData.reduce((acc, item) => {
      const key = item.prospectId || item.ApplicationId || item.id;
      if (!acc[key] || (parseFloat(item.eligibleLoanAmountByBm || 0) > parseFloat(acc[key].eligibleLoanAmountByBm || 0))) {
        acc[key] = item;
      }
      return acc;
    }, {});

    const uniqueData = Object.values(deduplicatedMap);

    return uniqueData.map((data) => {
      const summary = generateLoanSummaryForMember(data, fromDate, asAtDate);
      return summary || data;
    }).filter(Boolean);
  }, [reportData, fromDate, asAtDate]);

  const branchOptions = useMemo(() => {
    const uniqueBranches = [...new Set(flattenedData.map((data) =>
      data?.branchName || data?.branch || ""
    ))].filter(Boolean);
    return uniqueBranches.map((name) => ({ value: name, label: name }));
  }, [flattenedData]);

  const employeeOptions = useMemo(() => {
    const uniqueEmployees = [...new Set(flattenedData.map((data) =>
      data?.employeeName || data?.username || ""
    ))].filter(Boolean);
    return uniqueEmployees.map((name) => ({ value: name, label: name }));
  }, [flattenedData]);

  // Filter function
  const filterData = useCallback((data) => {
    let filteredData = [...data];

    // Date filter
    if (fromDateDisbursed || toDateDisbursed) {
      // Shared whole-day window, so this screen and Master cannot drift apart.
      const { start, end } = getReportWindow(fromDateDisbursed, toDateDisbursed);

      filteredData = filteredData.filter((item) => {
        if (!item.branchManagerStatusUpdatedAt) return false;
        const updatedAt = new Date(item.branchManagerStatusUpdatedAt);
        return updatedAt >= start && updatedAt <= end;
      });
    }

    // Branch filter
    if (selectedBranchFilter) {
      filteredData = filteredData.filter((item) => {
        const branchName = item?.branchName || item?.branch || "";
        return branchName === selectedBranchFilter.value;
      });
    }

    // Employee filter
    if (selectedEmployeeFilter) {
      filteredData = filteredData.filter((item) => {
        const employeeName = item?.employeeName || item?.username || "";
        return employeeName === selectedEmployeeFilter.value;
      });
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filteredData = filteredData.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    return filteredData;
  }, [fromDateDisbursed, toDateDisbursed, selectedBranchFilter, selectedEmployeeFilter, searchTerm]);

  const filteredData = useMemo(() => filterData(flattenedData), [filterData, flattenedData]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Event handlers
  const OpenSidebar = useCallback(() => {
    setOpenSidebarToggle(prev => !prev);
  }, []);

  const paginate = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
  }, []);

  const handleFromDateChange = useCallback((event) => {
    const val = event.target.value;
    setFromDateDisbursed(val);
    setCurrentPage(1); // Reset to first page when filter changes
    if (val && toDateDisbursed && selectedBranchFilter && selectedEmployeeFilter) {
      setTimeout(() => setShowMobileFilters(false), 300);
    }
  }, [toDateDisbursed, selectedBranchFilter, selectedEmployeeFilter]);

  const handleToDateChange = useCallback((event) => {
    const val = event.target.value;
    setToDateDisbursed(val);
    setCurrentPage(1);
    if (fromDateDisbursed && val && selectedBranchFilter && selectedEmployeeFilter) {
      setTimeout(() => setShowMobileFilters(false), 300);
    }
  }, [fromDateDisbursed, selectedBranchFilter, selectedEmployeeFilter]);

  const handleBranchFilterChange = useCallback((selectedOption) => {
    setSelectedBranchFilter(selectedOption);
    setCurrentPage(1);
    if (fromDateDisbursed && toDateDisbursed && selectedOption && selectedEmployeeFilter) {
      setTimeout(() => setShowMobileFilters(false), 300);
    }
  }, [fromDateDisbursed, toDateDisbursed, selectedEmployeeFilter]);

  const handleEmployeeFilterChange = useCallback((selectedOption) => {
    setSelectedEmployeeFilter(selectedOption);
    setCurrentPage(1);
    if (fromDateDisbursed && toDateDisbursed && selectedBranchFilter && selectedOption) {
      setTimeout(() => setShowMobileFilters(false), 300);
    }
  }, [fromDateDisbursed, toDateDisbursed, selectedBranchFilter]);

  // API call
  useEffect(() => {
    const getReportData = async (id) => {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${BASE_URL}/api/getGploutstandingreport`,
          { params: { id } }
        );

        if (response.data) {
          setReportData(response.data);
        } else {
          setError("No data received from server");
        }
      } catch (error) {
        console.error("Error fetching Data:", error);
        setError("Failed to fetch report data");
      } finally {
        setIsLoading(false);
      }
    };

    if (adminUser?.user?.id) {
      getReportData(adminUser.user.id);
    }
  }, [adminUser?.user?.id]);

  // Helper function to format date safely
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      console.warn("Invalid date format:", dateString);
      return "";
    }
  }, []);

  // Table columns configuration
  const columns = useMemo(() => [
    {
      label: "S No",
      render: (data, index, isExcel) => isExcel ? index + 1 : indexOfFirstItem + index + 1
    },
    {
      label: "Region",
      render: (data) => data?.regionName || data?.region || ""
    },
    {
      label: "Division",
      render: (data) => data?.divisionName || data?.division || ""
    },
    {
      label: "Branch Name",
      render: (data) => data?.branchName || data?.branch || ""
    },
    {
      label: "Branch Code",
      render: (data) => data?.branchCode || ""
    },
    {
      label: "Customer Name",
      render: (data) => data?.memberName || ""
    },
    {
      label: "Customer ID",
      render: (data) => data?.customerId || ""
    },
    {
      label: "Address",
      render: (data) => {
        const addressParts = [
          data?.permanentDoorNo,
          data?.permanentStreetNo,
          data?.permanentPanchayat,
          data?.permanentTaluk,
          data?.permanentDistrict,
          data?.permanentPincode
        ].filter(part => part && String(part).trim() !== "");

        return addressParts.length > 0 ? addressParts.join(", ") : "N/A";
      },
    },
    {
      label: "Contact No",
      render: (data) => data?.phoneNumber || ""
    },
    {
      label: "Loan Type",
      render: (data) => data?.loanType || ""
    },
    {
      label: "Sub Category",
      render: (data) => data?.businessDetails?.natureOfBusiness || ""
    },
    {
      label: "Loan Cycle",
      render: (data) => data?.loanCycle || data?.loanStyle || ""
    },
    {
      label: "Loan Account No",
      render: (data) => data?.loanId || ""
    },
    {
      label: "Rate of Interest",
      render: (data) => {
        const rate = data?.proposedLoanDetails?.rateOfInterest;
        return rate ? rate : "";
      },
    },
    {
      label: "Disbursement Date",
      render: (data) => {
        if (data?.branchManagerStatus === "disbursed" && data?.branchManagerStatusUpdatedAt) {
          return formatDate(data.branchManagerStatusUpdatedAt);
        }
        return "";
      },
    },
    {
      label: "Maturity Date",
      render: (data) => {
        try {
          if (typeof getFirstAndLastEmiDates === 'function') {
            const { lastEmiDate } = getFirstAndLastEmiDates(
              data?.branchManagerStatusUpdatedAt || data?.disbursementDate,
              data?.proposedLoanDetails?.tenureInMonths || data?.tenure,
              data?.emiDateByBranchManager || data?.emiDate,
              resolveEffectiveStartDate(data)
            );
            return lastEmiDate && !isNaN(lastEmiDate.getTime())
              ? lastEmiDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
              : "N/A";
          }
          return "N/A";
        } catch (error) {
          return "N/A";
        }
      },
    },
    {
      label: "Loan Amount",
      render: (data) => {
        const amount = getDisbursementAmount(data);
        return formatAmount(amount);
      },
    },
    {
      label: "Employee Name",
      render: (data) => data?.employeeName || data?.username || ""
    },
    {
      label: "Prospect ID",
      render: (data) => data?.ApplicationId || ""
    },
    {
      label: "Total Principal Paid",
      render: (data) => {
        const amount = data?.principalPaid;
        return formatAmount(amount);
      }
    },
    {
      label: "Total Interest Paid",
      render: (data) => {
        const amount = data?.interestPaid;
        return formatAmount(amount);
      }
    },
    {
      label: "Total EMI Paid",
      render: (data) => {
        const amount = data?.totalEmiPaid;
        return formatAmount(amount);
      }
    },
    {
      label: "Outstanding Principal",
      render: (data) => {
        const amount = data?.totalOutstandingPrincipal;
        return formatAmount(amount);
      }
    },
    {
      label: "Outstanding Interest",
      render: (data) => {
        const amount = data?.totalOutstandingInterest;
        return formatAmount(amount);
      }
    },
    {
      label: "DPD",
      render: (data) => data?.dpd ?? 0
    },
    {
      label: "Bucket",
      render: (data) => data?.bucket ?? "0"
    },
  ], [indexOfFirstItem, formatDate]);

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">Outstanding Report</h1>
            <div className="w-100">
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDateDisbursed}
                      onChange={handleFromDateChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDateDisbursed}
                      onChange={handleToDateChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>

                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={handleBranchFilterChange}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch Name"
                    className="filter-dropdown"
                    isDisabled={isLoading}
                  />
                </Col>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={handleEmployeeFilterChange}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                    isDisabled={isLoading}
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="alert alert-danger" role="alert">
                  Error: {error}
                </div>
              ) : (
                <DataTable
                  columns={columns}
                  data={currentItems}
                  reportName="GplOutstandingReport"
                  excelData={filteredData}
                />
              )}
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default GplOutstandingReport;